import React, { useEffect, useState } from 'react';
import { Mail, Clock, XCircle, Send, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { apiClient } from '@/services/api';

interface PendingInvitation {
  id: number;
  email: string;
  invited_by_name: string;
  created_at: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
}

export const PendingInvitationsList: React.FC = () => {
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);

  const fetchInvitations = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.getPendingInvitations();

      if (data.success) {
        setInvitations(data.data || []);
      } else {
        setError('Failed to load invitations');
      }
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  const handleCancel = async (invitationId: number, email: string) => {
    if (!confirm(`Are you sure you want to cancel the invitation for ${email}?`)) {
      return;
    }

    setCancellingId(invitationId);

    try {
      const data = await apiClient.cancelInvitation(invitationId);

      if (data.success) {
        // Remove from list
        setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      } else {
        alert('Failed to cancel invitation');
      }
    } catch (err: any) {
      alert(err.message || 'Network error');
    } finally {
      setCancellingId(null);
    }
  };

  const handleResend = async (invitationId: number, email: string) => {
    if (!confirm(`Resend invitation to ${email}?\n\nThis will extend the expiry date by 7 days and send a new email.`)) {
      return;
    }

    setResendingId(invitationId);

    try {
      const data = await apiClient.resendInvitation(invitationId);

      if (data.success) {
        // Refresh the list to show updated expiry
        await fetchInvitations();
        alert('Invitation resent successfully!');
      } else {
        alert('Failed to resend invitation');
      }
    } catch (err: any) {
      alert(err.message || 'Network error');
    } finally {
      setResendingId(null);
    }
  };

  const getStatusBadge = (status: string, expiresAt: string) => {
    const isExpired = new Date(expiresAt) < new Date();

    if (isExpired) {
      return (
        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
          Expired
        </span>
      );
    }

    switch (status) {
      case 'pending':
        return (
          <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
            Pending
          </span>
        );
      case 'accepted':
        return (
          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
            Accepted
          </span>
        );
      case 'revoked':
        return (
          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className="text-center py-12">
        <Mail className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium mb-1">No pending invitations</p>
        <p className="text-gray-500 text-sm">
          All sent invitations have been accepted or cancelled
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">
          {invitations.length} pending invitation{invitations.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={fetchInvitations}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Invitations List */}
      {invitations.map((invitation) => {
        const isExpired = new Date(invitation.expires_at) < new Date();

        return (
          <div
            key={invitation.id}
            className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
          >
            <div className="flex items-start justify-between">
              {/* Invitation Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <h4 className="font-semibold text-gray-900">{invitation.email}</h4>
                  {getStatusBadge(invitation.status, invitation.expires_at)}
                </div>

                {/* Details */}
                <div className="grid grid-cols-3 gap-4 text-xs mt-3">
                  <div>
                    <p className="text-gray-500 mb-0.5">Sent</p>
                    <p className="font-medium text-gray-900">
                      {formatDistanceToNow(new Date(invitation.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-0.5">Expires</p>
                    <p className={`font-medium ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                      {isExpired ? 'Expired' : formatDistanceToNow(new Date(invitation.expires_at), { addSuffix: true })}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-0.5">Invited by</p>
                    <p className="font-medium text-gray-900">
                      {invitation.invited_by_name || 'Unknown'}
                    </p>
                  </div>
                </div>

                {/* Expiry Warning */}
                {isExpired && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-800">
                      This invitation has expired. The recipient can no longer use this link.
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="ml-4 flex flex-col gap-2">
                {/* Resend button - shown for pending/expired invitations that haven't been accepted */}
                {(invitation.status === 'pending' || isExpired) && invitation.status !== 'accepted' && (
                  <button
                    onClick={() => handleResend(invitation.id, invitation.email)}
                    disabled={resendingId === invitation.id}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {resendingId === invitation.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Resend
                  </button>
                )}

                {/* Cancel button - only for pending invitations that aren't expired */}
                {invitation.status === 'pending' && !isExpired && (
                  <button
                    onClick={() => handleCancel(invitation.id, invitation.email)}
                    disabled={cancellingId === invitation.id}
                    className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {cancellingId === invitation.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <p className="text-blue-900 text-sm font-medium mb-1">About Invitations</p>
        <ul className="text-blue-800 text-xs space-y-1">
          <li>• Invitations expire after 7 days</li>
          <li>• Use the "Resend" button to extend expiry and resend the email</li>
          <li>• Cancelled invitations cannot be reused or resent</li>
          <li>• Accepted invitations appear in the Active Auditors tab</li>
        </ul>
      </div>
    </div>
  );
};
